import React, { useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useSwipeable } from "react-swipeable";
import { ImageDto } from "@/data/dtos/image.dto";
import { useTranslation } from "react-i18next";
import ImagePickerModal from "./core/ImagePickerModel";
import { logger } from "@/lib/default-logger";

interface ImagesUploadProps {
  images: ImageDto[];
  onChange: (field: string, value: any) => void;
  title?: string;
}

const UploadSection: React.FC<ImagesUploadProps> = ({
  images,
  onChange,
  title = "Kaynak Ekle",
}) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  // Intersection Observer for lazy loading
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLImageElement;
            target.src = target.dataset.src || "";
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1 }
    );

    imgRefs.current.forEach((img) => {
      if (img) observer.observe(img);
    });

    return () => {
      imgRefs.current.forEach((img) => {
        if (img) observer.unobserve(img);
      });
    };
  }, [images]);

  // ✅ Delete Image
  const handleDeleteImage = useCallback(
    async (id: string) => {
      try {
        const updatedImages = images.filter((image) => image._id !== id);
        onChange("images", updatedImages);
      } catch (error) {
        console.error("Failed to delete image:", error);
        alert("Failed to delete image. Please try again.");
      }
    },
    [onChange, images]
  );

  // ✅ Add Image
  const handleImageSelect = useCallback(
    (image: ImageDto) => {
      if (!images.some((img) => img._id === image._id)) {
        // Directly update the parent state using onChange
        onChange("images", [...images, image]);
      }
      setIsOpen(false);
    },
    [images, onChange]
  );

  const handlers = useSwipeable({
    onSwipedLeft: (eventData) => logger.log("swiped left", eventData),
  });

  // ✅ Swipe-to-Delete for Mobile
  const renderImages = useMemo(
    () =>
      images.map((image, index) => {
        return (
          <Grid item key={image._id} xs={6} sm={4} md={3} lg={2}>
            <Box {...handlers}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "100%",
                  overflow: "hidden",
                  borderRadius: 2,
                  boxShadow: 2,
                  backgroundColor: "#f0f0f0",
                }}
              >
                <img
                  ref={(el) => (imgRefs.current[index] = el)}
                  data-src={image.url}
                  alt={image.altText || "Uploaded"}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />

                {/* ✅ Delete Button */}
                <IconButton
                  onClick={() => handleDeleteImage(image._id!)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    color: "red",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        );
      }),
    [images, handleDeleteImage]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}

      {/* ✅ Image Grid */}
      <Grid container spacing={2} className="flex gap-4">
        {renderImages}

        {/* ✅ Add Button */}
        <Grid
          item
          xs={6}
          sm={4}
          md={3}
          lg={2}
          className="border border-dashed border-gray-500 rounded flex justify-center items-center"
          onClick={() => setIsOpen(true)}
        >
          <Box sx={{ cursor: "pointer" }}>
            <Typography>+ {t("add_image")}</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ✅ Image Picker Modal */}
      <ImagePickerModal
        isOpen={isOpen}
        onSelect={handleImageSelect}
        onClose={() => setIsOpen(false)}
      />
    </Box>
  );
};

export default UploadSection;
